// @ts-check
import { WORLD, BOTS } from '../../game/tuning.js';
const T = WORLD.tile;
/** Small authoring vocabulary, never random geometry. All positions below are tile units.
 * @param {number} world @param {number} stage @param {string} name @param {string} brief @param {number} [length]
 */
export function route(world, stage, name, brief, length = 144) {
  /** @type {import('../../game/types.js').LevelDefinition} */
  const d = { id: `${world}-${stage}`, world, stage, name, brief, width: (length + 8) * T, height: 20 * T, spawn: 3 * T, finish: length * T, solids: [], enemies: [], gaps: [], mechanisms: [], checkpoints: [] };
  const solids = /** @type {import('../../game/types.js').SolidDefinition[]} */ (d.solids);
  const enemies = /** @type {import('../../game/types.js').EnemySpawn[]} */ (d.enemies);
  const mechanisms = /** @type {import('../../game/types.js').MechanismDefinition[]} */ (d.mechanisms);
  let serial = 0;
  const id = () => `${d.id}-${serial++}`;
  const api = {
    d,
    /** @param {number} x @param {number} y @param {number} [w] @param {number} [h] @param {import('../../game/types.js').SolidKind} [kind] */
    platform(x, y, w = 1, h = 1, kind = 'compute') { solids.push({ id: id(), x: x * T, y: y * T, width: w * T, height: h * T, kind }); return api; },
    /** @param {number} x @param {number} [y] @param {import('../../game/types.js').Reward} [reward] @param {string} [signal] */
    node(x, y = 3, reward = 'upgrade', signal) { solids.push({ id: id(), x: x * T, y: y * T, width: T, height: T, kind: 'node', reward, signal }); return api; },
    /** @param {number} x @param {number} [y] @param {boolean} [sentinel] */
    bot(x, y = 0, sentinel = false) { enemies.push({ id: id(), x: x * T, y: y * T, vx: -BOTS.patrolSpeed, kind: sentinel ? 'sentinel' : 'drone' }); return api; },
    /** @param {number} x @param {number} w */
    gap(x, w) { /** @type {[number,number][]} */ (d.gaps).push([x * T, (x + w) * T]); return api; },
    /** @param {number} x @param {string} name */
    checkpoint(x, name) { /** @type {import('../../game/types.js').Checkpoint[]} */ (d.checkpoints).push({ id: `checkpoint-${d.checkpoints?.length}`, x: x * T, name }); return api; },
    /** @param {import('../../game/types.js').MechanismKind} kind @param {number} x @param {number} y @param {number} w @param {number} h @param {number} [period] @param {{travelX?:number,travelY?:number,offset?:number,group?:string,alternate?:boolean}} [options] */
    device(kind, x, y, w, h, period = 4, options = {}) { mechanisms.push({ id: id(), kind, x: x * T, y: y * T, width: w * T, height: h * T, period, ...options, travelX: (options.travelX ?? 0) * T, travelY: (options.travelY ?? 0) * T }); return api; },
    /** @param {number} x @param {number} count @param {boolean} [down] */
    stairs(x, count, down = false) { for (let i = 0; i < count; i++) api.platform(x + i, 0, 1, down ? count - i : i + 1); return api; },
    /** @param {import('../../game/types.js').BossKind} kind @param {string} name @param {number} [health] */
    boss(kind, name, health = 4) {
      const left = length - 20, right = length - 3;
      d.boss = { kind, name, left: left * T, right: right * T, x: (right - 5) * T, health };
      api.checkpoint(left - 4, `${name} access`);
      api.node(left - 6, 3, 'upgrade');
      api.node(left + 3, 3, 'bit', ['gatekeeper', 'switchboard', 'raster', 'null'].includes(kind) ? 'boss' : undefined);
      api.platform(left + 7, 1, 2, 0.5).platform(right - 5, 1, 2, 0.5);
      return api;
    },
    build() {
      for (const values of [d.solids, d.enemies, d.gaps, d.mechanisms ?? [], d.checkpoints ?? []]) { values.forEach(Object.freeze); Object.freeze(values); }
      if (d.boss) Object.freeze(d.boss);
      return Object.freeze(d);
    },
  };
  return api;
}
